import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { GainsMonitorPage as GainsMonitor } from '../components/GainsMonitorPage';
import { useProjectContext } from '../context/ProjectContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import GainsMonitorService from '../services/gainsMonitorService';
import { useGestaoKPIs } from '../modules/gestao/hooks/useGestaoKPIs';

import OperationalMetricsBar from '../components/operational/OperationalMetricsBar';
import { useOperationalContext } from '../contexts/OperationalContext';

/**
 * Página Ganhos - Monitor de Ganhos conectado ao Workflow
 */
export const GanhosPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedProject } = useProjectContext();
  const organizationId = useOrganizationId();
  const [selectedKpi, setSelectedKpi] = useState<'all' | 'cost' | 'lead'>('all');
  const executionIdParam = searchParams.get('executionId');
  const projectIdParam = searchParams.get('projectId') || selectedProject?.id;
  const stageIdParam = searchParams.get('stageId') || undefined;

  const { metrics } = useGestaoKPIs({ organizationId });
  const { setOperationalMetrics, setActiveExecution } = useOperationalContext();

  const kaizensQuery = useQuery({
    queryKey: ['ganhos', 'origins', organizationId, projectIdParam, stageIdParam],
    enabled: Boolean(organizationId),
    queryFn: () => GainsMonitorService.getKaizens({
      organization_id: organizationId,
      project_id: projectIdParam,
      stage_id: stageIdParam,
    }),
    staleTime: 30_000,
  });

  const gainsSummaryQuery = useQuery({
    queryKey: ['ganhos', 'summary', organizationId, projectIdParam],
    enabled: Boolean(organizationId),
    queryFn: () => GainsMonitorService.getDashboardData(organizationId),
    staleTime: 30_000,
  });

  useEffect(() => {
    setOperationalMetrics({
      globalSla: metrics?.sla_rate_7d ?? 0,
      throughput7d: metrics?.throughput_7d ?? 0,
      userWip: metrics?.avg_wip_per_user ?? 0,
      bottleneckStage: metrics?.most_critical_stage ?? null,
    });
  }, [
    metrics?.avg_wip_per_user,
    metrics?.most_critical_stage,
    metrics?.sla_rate_7d,
    metrics?.throughput_7d,
    setOperationalMetrics,
  ]);

  const gainRows = useMemo(() => {
    return (kaizensQuery.data ?? []).map((kaizen) => {
      const raw = kaizen as any;
      const executionId = raw.execution_id ?? raw.execution_origin_id ?? raw.metadata?.execution_id ?? null;
      const flowOrigin = raw.flow_name ?? raw.workflow_name ?? raw.workflow_id ?? 'Fluxo não informado';
      const executionOrigin = executionId ?? raw.execution_code ?? 'Execução não informada';
      const stageOptimized = raw.optimized_stage ?? raw.stage_name ?? raw.metadata?.stage_name ?? 'Etapa não informada';
      const before = kaizen.baseline_lead_time ?? 0;
      const after = kaizen.current_lead_time ?? kaizen.target_lead_time ?? 0;

      return {
        id: kaizen.id,
        title: kaizen.title,
        flowOrigin,
        executionOrigin,
        executionId,
        stageOptimized,
        before,
        after,
        costSaving: Number(raw.cost_saving ?? 0),
      };
    });
  }, [kaizensQuery.data]);

  const filteredRows = useMemo(() => {
    const scopedRows = executionIdParam ? gainRows.filter((row) => row.executionId === executionIdParam) : gainRows;
    if (selectedKpi === 'cost') {
      return scopedRows.filter((row) => row.costSaving > 0);
    }
    if (selectedKpi === 'lead') {
      return scopedRows.filter((row) => row.before > row.after);
    }
    return scopedRows;
  }, [executionIdParam, gainRows, selectedKpi]);

  type GainSortKey = 'title' | 'flow' | 'execution' | 'stage' | 'delta';
  type GainSortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<GainSortKey>('delta');
  const [sortDir, setSortDir] = useState<GainSortDir>('desc');

  const handleSortChange = useCallback((key: GainSortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      const textual: GainSortKey[] = ['title', 'flow', 'execution', 'stage'];
      setSortDir(textual.includes(key) ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return (a.title ?? '').localeCompare(b.title ?? '') * dir;
        case 'flow':
          return (a.flowOrigin ?? '').localeCompare(b.flowOrigin ?? '') * dir;
        case 'execution':
          return String(a.executionOrigin ?? '').localeCompare(String(b.executionOrigin ?? '')) * dir;
        case 'stage':
          return (a.stageOptimized ?? '').localeCompare(b.stageOptimized ?? '') * dir;
        case 'delta':
          return ((a.before - a.after) - (b.before - b.after)) * dir;
        default:
          return 0;
      }
    });
  }, [filteredRows, sortKey, sortDir]);

  const gainColumns: ReadonlyArray<{ key: GainSortKey | null; label: string; align?: string }> = [
    { key: 'title', label: 'Ganho', align: 'text-left' },
    { key: 'flow', label: 'Fluxo origem', align: 'text-left' },
    { key: 'execution', label: 'Execução origem', align: 'text-left' },
    { key: 'stage', label: 'Etapa otimizada', align: 'text-left' },
    { key: 'delta', label: 'Antes vs Depois', align: 'text-left' },
    { key: null, label: '' },
  ];

  return (
    <div className="flex h-full w-full flex-col overflow-hidden dark:bg-black">

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex w-full flex-col">
          <OperationalMetricsBar />

          <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedKpi('cost')}
            className={`rounded px-3 py-2 text-xs font-semibold transition ${selectedKpi === 'cost' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50'}`}
          >
            Economia acumulada: {GainsMonitorService.formatCurrency(gainsSummaryQuery.data?.total_cost_savings ?? 0)}
          </button>
          <button
            type="button"
            onClick={() => setSelectedKpi('lead')}
            className={`rounded px-3 py-2 text-xs font-semibold transition ${selectedKpi === 'lead' ? 'bg-blue-600 text-white shadow-sm' : 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'}`}
          >
            Lead time médio reduzido: {(gainsSummaryQuery.data?.total_lead_time_reduction ?? 0).toFixed(0)} min
          </button>
          <button
            type="button"
            onClick={() => setSelectedKpi('all')}
            className={`rounded px-3 py-2 text-xs font-semibold transition ${selectedKpi === 'all' ? 'bg-slate-700 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`}
          >
            Exibir todos os ganhos
          </button>
          </div>

          <div className="enterprise-table-shell">
            <table className="w-full text-sm">
              <thead>
                <tr className="enterprise-table-header">
                  {gainColumns.map((col, idx) => {
                    if (!col.key) {
                      return <th key={`spacer-${idx}`} className="enterprise-table-head-cell" />;
                    }
                    const isActive = sortKey === col.key;
                    const Indicator = isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                    return (
                      <th key={col.key} className={`enterprise-table-head-cell ${col.align ?? ''}`}>
                        <button
                          type="button"
                          onClick={() => handleSortChange(col.key as GainSortKey)}
                          className={`group inline-flex items-center gap-1 transition-colors ${
                            isActive ? 'enterprise-text-primary' : 'enterprise-text-secondary hover:enterprise-text-primary'
                          }`}
                          title={`Ordenar por ${col.label}`}
                        >
                          <span>{col.label}</span>
                          <Indicator size={10} className={isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'} />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.id} className="enterprise-table-row">
                    <td className="enterprise-table-cell font-medium">{row.title}</td>
                    <td className="enterprise-table-cell enterprise-text-secondary">{row.flowOrigin}</td>
                    <td className="enterprise-table-cell enterprise-text-secondary">{row.executionOrigin}</td>
                    <td className="enterprise-table-cell enterprise-text-secondary">{row.stageOptimized}</td>
                    <td className="enterprise-table-cell">{row.before.toFixed(0)} min → {row.after.toFixed(0)} min</td>
                    <td className="enterprise-table-cell text-right">
                      <button
                        type="button"
                        onClick={() => {
                          if (!row.executionId) return;
                          setActiveExecution(row.executionId, row.executionOrigin, null, row.flowOrigin);
                          navigate(`/workflow?executionId=${encodeURIComponent(row.executionId)}`);
                        }}
                        disabled={!row.executionId}
                        className="rounded border border-[var(--border-default)] px-2 py-1 text-xs font-medium text-[var(--brand-primary)] disabled:opacity-50"
                      >
                        Ganho → Execução origem
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="min-h-0 flex-1">
            <GainsMonitor
              organizationId={organizationId}
              projectId={projectIdParam}
              stageId={stageIdParam}
              onGoHome={() => navigate('/centro-operacional')}
              onBackToProject={() => navigate('/gestao')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
